import React, { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ConfirmButton } from "@/components/ConfirmButton";
import { UserAccessActions } from "@/components/UserAccessActions";
import { WelcomeInviteAll } from "@/components/WelcomeInvite";
import { Network, Wallet, Send, Users, ShoppingCart, ArrowUpRight, ArrowDownLeft, ChevronDown, ChevronRight, Settings } from "lucide-react";
import { MasterInvite } from "@/components/MasterInvite";
import { NetworkDealerSheet } from "@/components/NetworkDealerSheet";
import { SimQuickMove } from "@/components/sim/SimQuickMove";
import { useAuth } from "@/context/AuthContext";
import { CreditCard } from "lucide-react";

const eur = (n) => euro(n);

export default function MasterDealer() {
  const [balance, setBalance] = useState(0);
  const [netOpen, setNetOpen] = useState(null);
  const [summary, setSummary] = useState({ dealers_count: 0, network_balance: 0, network_spent: 0, total_transferred: 0 });
  const [network, setNetwork] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [wTarget, setWTarget] = useState(null);
  const [wAmount, setWAmount] = useState("");
  const [aggio, setAggio] = useState(null);
  const openWithdraw = (d) => { setWTarget(d); setWAmount(""); setAggio(null); api.get(`/master/dealers/${d.id}/aggio`).then((r) => setAggio(r.data)).catch(() => {}); };
  const [sTarget, setSTarget] = useState(null);
  const [simTarget, setSimTarget] = useState(null);
  const { user } = useAuth();
  const focusId = new URLSearchParams(window.location.search).get("d");
  const [sDirect, setSDirect] = useState(false);
  const [sPct, setSPct] = useState("");
  const [sBusy, setSBusy] = useState(false);
  const openSettings = (d) => { setSTarget(d); setSDirect(!!d.allow_direct_topup); setSPct(d.master_recharge_pct != null ? String(d.master_recharge_pct) : ""); };
  const saveSettings = async () => {
    setSBusy(true);
    try {
      const body = { allow_direct_topup: sDirect };
      if (sPct !== "") body.master_recharge_pct = parseFloat(sPct) || 0;
      await api.put(`/master/dealers/${sTarget.id}/settings`, body);
      toast.success("Impostazioni dealer salvate");
      setSTarget(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSBusy(false); }
  };
  const [expanded, setExpanded] = useState(null);
  const [custMap, setCustMap] = useState({});
  const [custLoading, setCustLoading] = useState(null);
  const toggleCustomers = async (d) => {
    if (expanded === d.id) { setExpanded(null); return; }
    setExpanded(d.id);
    if (!custMap[d.id]) {
      setCustLoading(d.id);
      try { const { data } = await api.get(`/master/dealers/${d.id}/customers`); setCustMap((m) => ({ ...m, [d.id]: data })); }
      catch { toast.error("Impossibile caricare i clienti"); }
      finally { setCustLoading(null); }
    }
  };

  const load = () => Promise.all([
    api.get("/master/dealers").then((r) => { setBalance(r.data.wallet_balance); setSummary(r.data.summary); setDealers(r.data.dealers); setNetwork(r.data.network_status); }),
    api.get("/master/transfers").then((r) => setTransfers(r.data)),
  ]).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (focusId && dealers.some((x) => x.id === focusId)) { document.querySelector(`[data-testid="sub-dealer-${focusId}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }); } }, [focusId, dealers]);

  const transfer = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Importo non valido"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/master/transfer", { dealer_id: target.id, amount: amt });
      toast.success(`${eur(data.credited)} accreditati a ${target.name} (scalati ${eur(data.debited)} dal tuo Plafond)`);
      setTarget(null); setAmount("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel trasferimento"); }
    finally { setBusy(false); }
  };

  const withdraw = async () => {
    const amt = parseFloat(wAmount);
    if (!amt || amt <= 0) { toast.error("Importo non valido"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/master/withdraw", { dealer_id: wTarget.id, amount: amt });
      toast.success(`Prelevati ${eur(data.amount)} da ${wTarget.name}${data.aggio_reversed ? ` · aggio stornato ${eur(data.aggio_reversed)}, a te tornano ${eur(data.refund)}` : ""} → il tuo wallet: ${eur(data.master_balance)} · dealer: ${eur(data.dealer_balance)}`);
      setWTarget(null); setWAmount("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel prelievo"); }
    finally { setBusy(false); }
  };

  const stats = [
    { icon: Wallet, label: "Il tuo Plafond servizi", value: eur(balance), cls: "bg-primary/15 text-primary" },
    { icon: Users, label: "Anagrafiche collegate", value: summary.dealers_count, cls: "bg-indigo-500/15 text-indigo-400" },
    { icon: ShoppingCart, label: "Venduto dalla rete", value: eur(summary.network_spent), cls: "bg-teal-600/15 text-teal-500" },
    { icon: ArrowUpRight, label: "Plafond trasferito", value: eur(summary.total_transferred), cls: "bg-fuchsia-600/15 text-fuchsia-400" },
  ];

  return (
    <div className="space-y-6" data-testid="master-dealer-page">
      <div>
        <div className="flex items-center gap-2"><Network className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Master Dealer</span></div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Cruscotto rete</h1>
        <p className="text-sm text-muted-foreground mt-1">Monitora i tuoi dealer, i loro consumi e i trasferimenti di Plafond.</p>
      </div>

      {network && <Card className={`p-4 space-y-1 ${network.below_threshold ? "border-amber-500/40 bg-amber-500/10" : "border-teal-500/40 bg-teal-500/10"}`} data-testid="master-network-policy"><p className="text-sm font-semibold" data-testid="master-network-count">{network.registered_shops}/{network.minimum_shops} negozi registrati e collegati</p><p className="text-xs text-muted-foreground" data-testid="master-network-policy-note">{network.below_threshold ? `${network.missing_shops === 1 ? "Manca" : "Mancano"} ${network.missing_shops} ${network.missing_shops === 1 ? "negozio" : "negozi"} alla soglia prevista. L’Admin riceve una segnalazione e decide: il ruolo Master non viene revocato automaticamente.` : "Soglia della rete raggiunta."} I contatti potenziali e gli account archiviati non vengono conteggiati. Il riepilogo delle anagrafiche collegate include anche gli archiviati.</p></Card>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="master-summary">
        {stats.map((s) => (
          <Card key={s.label} className="p-4 flex items-center gap-3" data-testid={`master-stat-${s.label}`}>
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${s.cls}`}><s.icon className="w-5 h-5" /></div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground truncate">{s.label}</p>
              <p className="font-head text-xl font-bold">{s.value}</p>
            </div>
          </Card>
        ))}
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3"><h2 className="font-head text-lg font-semibold">I miei dealer</h2><div className="flex flex-wrap gap-2"><WelcomeInviteAll /><MasterInvite /></div></div>
        <div className="grid md:grid-cols-2 gap-4">
          {dealers.map((d) => (
            <Card key={d.id} className={`p-5 space-y-4 ${focusId === d.id ? "ring-2 ring-primary" : ""}`} data-testid={`sub-dealer-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button type="button" onClick={() => setNetOpen(d)} className="font-head font-semibold truncate hover:text-primary underline-offset-4 hover:underline text-left" title="Apri scheda dealer" data-testid={`sub-dealer-open-${d.id}`}>{d.name}</button>
                  <p className="text-sm text-muted-foreground truncate">{d.email}</p>
                </div>
                <div className="flex flex-wrap justify-end gap-1.5 shrink-0">
                  {d.status && <Badge variant="outline" className="text-xs cursor-help" title={d.status === "approved" ? "Account approvato: il dealer può lavorare" : d.status === "pending" ? "Account in attesa di approvazione da parte dell'admin (non riguarda soldi o guadagni)" : `Stato account: ${d.status}`} data-testid={`sub-dealer-status-${d.id}`}>{d.status === "approved" ? "Attivo" : d.status === "pending" ? "Account in attesa" : d.status}</Badge>}
                  {d.allow_direct_topup && <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">Acquisto diretto</Badge>}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 rounded-lg bg-secondary/50 p-3 text-center" data-testid={`sub-dealer-money-${d.id}`}>
                {[["Plafond", eur(d.wallet_balance), "Plafond servizi disponibile adesso sul conto del dealer"], ["Venduto", eur(d.spent_total), `Valore dei servizi venduti dal dealer: ${d.orders_count} ordini completati`], ["Ricevuto", eur(d.received_total), "Totale Plafond che gli hai trasferito finora, aggio compreso"], ["Aggio", `${d.effective_pct ?? 0}%`, "Percentuale in più che il dealer riceve a ogni tuo trasferimento (la paghi tu). La cambi in Impostazioni"]].map(([l, v, tip]) => <div key={l} className="min-w-0 cursor-help" title={tip} data-testid={`sub-dealer-${l.toLowerCase()}-${d.id}`}>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</p><p className="font-semibold text-sm truncate">{v}</p></div>)}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" className="sim-chip gap-1" onClick={() => { setTarget(d); setAmount(""); }} data-testid={`transfer-open-${d.id}`}><Send className="w-4 h-4" /> Trasferisci</Button>
                <Button size="sm" variant="outline" className="sim-chip gap-1" onClick={() => openWithdraw(d)} data-testid={`withdraw-open-${d.id}`}><ArrowDownLeft className="w-4 h-4" /> Preleva</Button>
                <Button size="sm" variant="outline" className="sim-chip gap-1" onClick={() => setSimTarget(d)} data-testid={`sim-move-open-${d.id}`}><CreditCard className="w-4 h-4" /> Sposta SIM</Button>
                <Button size="sm" variant="ghost" className="sim-chip gap-1" onClick={() => openSettings(d)} data-testid={`settings-open-${d.id}`}><Settings className="w-4 h-4" /> Impostazioni</Button>
                <UserAccessActions user={d} mode="manager" />
                <button onClick={() => toggleCustomers(d)} className="ml-auto flex items-center gap-1 text-sm text-primary hover:underline" data-testid={`customers-toggle-${d.id}`}>
                  {expanded === d.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}<Users className="w-4 h-4" /> Clienti ({d.customers_count || 0})
                </button>
              </div>
              {expanded === d.id && (
                <div className="rounded-lg border border-border overflow-hidden" data-testid={`customers-list-${d.id}`}>
                  {custLoading === d.id ? (
                    <p className="text-sm text-muted-foreground p-3">Caricamento…</p>
                  ) : (custMap[d.id] || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground p-3">Nessun cliente registrato da questo dealer.</p>
                  ) : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Contatti</TableHead><TableHead className="text-right">Ordini · Speso</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {(custMap[d.id] || []).map((c) => (
                          <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                            <TableCell className="font-medium">{c.name}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{c.email || "—"}{c.phone && <> · <PhoneDisplay value={c.phone} data-testid={`master-customer-phone-${c.id}`} /></>}</TableCell>
                            <TableCell className="text-right text-sm">{c.orders_count} · {eur(c.spent_total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}
            </Card>
          ))}
          {dealers.length === 0 && <p className="text-muted-foreground">Nessun dealer collegato. Contatta l'amministratore per assegnarne.</p>}
        </div>
      </div>

      <div>
        <h2 className="font-head text-lg font-semibold mb-3">Storico trasferimenti</h2>
        <Card className="glow-border">
          <Table>
            <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Descrizione</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
            <TableBody>
              {transfers.map((t, i) => (
                <TableRow key={i} data-testid={`transfer-row-${i}`}>
                  <TableCell className="text-sm text-muted-foreground">{(t.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell>{t.description || "Trasferimento"}</TableCell>
                  <TableCell className={`text-right font-medium ${t.amount < 0 ? "text-rose-400" : "text-teal-500"}`}>{t.amount < 0 ? "−" : "+"}{eur(Math.abs(t.amount))}</TableCell>
                </TableRow>
              ))}
              {transfers.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Nessun trasferimento effettuato.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={!!sTarget} onOpenChange={(o) => !o && setSTarget(null)}>
        <DialogContent data-testid="settings-dialog">
          <DialogHeader><DialogTitle>Impostazioni · {sTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm">Consenti acquisto diretto</p><p className="text-xs text-muted-foreground">Se attivo, il dealer può ricaricare da solo al prezzo standard.</p></div>
              <Switch checked={sDirect} onCheckedChange={setSDirect} data-testid="settings-direct" />
            </div>
            <div>
              <Label>% ricarica riconosciuta al dealer</Label>
              <p className="text-xs text-muted-foreground mb-1.5">Bonus Plafond che il dealer riceve quando gli trasferisci Plafond. Vuoto = usa il default di sistema.</p>
              <Input type="number" min="0" step="0.5" value={sPct} onChange={(e) => setSPct(e.target.value)} placeholder="es. 3" data-testid="settings-pct" />
            </div>
            <Button className="w-full" disabled={sBusy} onClick={saveSettings} data-testid="settings-save">{sBusy ? "Salvataggio…" : "Salva impostazioni"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Trasferisci Plafond a {target?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Disponibile: {eur(balance)} · aggio a questo dealer: {target?.effective_pct ?? 0}%</p>
            <div><Label>Importo (€)</Label><Input type="number" min="0" step="0.01" className="mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="transfer-amount" /></div>
            {(() => { const a = parseFloat(amount) || 0; const b = Math.round(a * (target?.effective_pct || 0)) / 100; return a > 0 && <div className="rounded-lg bg-secondary/50 p-3 text-sm space-y-0.5" data-testid="transfer-preview">
              <div className="flex justify-between"><span>Importo</span><span>{eur(a)}</span></div>
              <div className="flex justify-between"><span>Aggio {target?.effective_pct ?? 0}%</span><span>+ {eur(b)}</span></div>
              <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Al dealer arrivano · dal tuo Plafond escono</span><span data-testid="transfer-preview-total">{eur(a + b)}</span></div>
              {a + b > balance && <p className="text-xs text-rose-500">Plafond insufficiente per importo + aggio.</p>}</div>; })()}
            <ConfirmButton className="w-full gap-2" disabled={busy || (parseFloat(amount) || 0) * (1 + (target?.effective_pct || 0) / 100) > balance} onConfirm={transfer} data-testid="transfer-submit" title="Confermi il trasferimento?" description={`Al dealer ${target?.name} arrivano ${eur((parseFloat(amount) || 0) * (1 + (target?.effective_pct || 0) / 100))} (aggio incluso), scalati dal tuo Plafond.`} confirmText="Trasferisci"><Send className="w-4 h-4" />{busy ? "Trasferimento…" : "Conferma trasferimento"}</ConfirmButton>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!wTarget} onOpenChange={(o) => !o && setWTarget(null)}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader><DialogTitle>Preleva Plafond da {wTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Plafond servizi del dealer: {eur(wTarget?.wallet_balance || 0)}. L'importo prelevato torna sul tuo Plafond al netto dell'aggio che avevi dato (aggio inverso).</p>
            {aggio && <div className="rounded-lg bg-secondary/50 p-3 text-sm space-y-1" data-testid="withdraw-aggio">
              <p>Aggio medio dato: <strong data-testid="withdraw-aggio-avg">{aggio.aggio_avg_pct}%</strong> su {eur(aggio.aggio_paid_total)} ({eur(aggio.aggio_bonus_total)} di aggio)</p>
              <div className="max-h-32 overflow-y-auto text-xs text-muted-foreground divide-y divide-border">{aggio.history.map((h, i) => <div key={i} className="py-1 flex justify-between gap-2" data-testid={`withdraw-aggio-row-${i}`}>
                <span>{new Date(h.created_at).toLocaleDateString("it-IT")} · {h.type === "transfer_in" ? `Trasferimento${h.pct != null ? ` aggio ${h.pct}%` : ""}` : "Prelievo"}</span><span>{h.amount > 0 ? "+" : "−"}{eur(Math.abs(h.amount))}</span></div>)}
                {aggio.history.length === 0 && <p className="py-1">Nessun movimento.</p>}</div></div>}
            <div><Label>Importo (€)</Label><Input type="number" min="0" step="0.01" className="mt-1" value={wAmount} onChange={(e) => setWAmount(e.target.value)} data-testid="withdraw-amount" /></div>
            {(() => { const amt = parseFloat(wAmount) || 0; const tr = (aggio?.aggio_paid_total || 0) + (aggio?.aggio_bonus_total || 0); const back = aggio?.aggio_paid_total > 0 && amt > 0 ? Math.round(Math.min(amt, tr) * aggio.aggio_bonus_total / tr * 100) / 100 : 0; return amt > 0 && <p className="text-sm" data-testid="withdraw-preview">Al dealer tolti <strong>{eur(amt)}</strong>{back > 0 && <> · aggio stornato <strong>{eur(back)}</strong></>} · a te tornano <strong className="text-emerald-500">{eur(amt - back)}</strong></p>; })()}
            <ConfirmButton className="w-full gap-2" disabled={busy} onConfirm={withdraw} data-testid="withdraw-submit" title="Confermi il prelievo?" description={`Verranno prelevati ${eur(parseFloat(wAmount) || 0)} dal wallet di ${wTarget?.name}; sul tuo torna l'importo al netto dell'aggio dato.`} confirmText="Preleva"><ArrowDownLeft className="w-4 h-4" />{busy ? "Prelievo…" : "Conferma prelievo"}</ConfirmButton>
          </div>
        </DialogContent>
      </Dialog>
      <NetworkDealerSheet dealer={netOpen} onClose={() => setNetOpen(null)} />
      <SimQuickMove key={simTarget?.id || "none"} dealer={simTarget} meId={user?.id} onClose={() => setSimTarget(null)} onDone={load} />
    </div>
  );
}
